import React from 'react';
import {
  Box,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { SessionDetailsCube } from './session-details-cube';
import { ContainerState, K8sPod } from './k8s-analyzer-types';

interface PodDetailsDrawerProps {
  pod: K8sPod | null;
  open: boolean;
  onClose: () => void;
}

const getContainerStateString = (state: ContainerState): string => {
  if (!state) return 'Unknown';
  if (state.running) return 'Running';
  if (state.waiting?.reason) return state.waiting.reason;
  if (state.terminated?.reason) return state.terminated.reason;
  return 'Unknown';
};

const getSessionStatusColor = (status: string | undefined | null) => {
  if (!status || typeof status !== 'string') return 'default';
  
  switch (status.toLowerCase()) {
    case 'connected':
      return 'success';
    case 'qrcode':
      return 'info';
    case 'initializing':
    case 'browser_started':
    case 'navigating':
      return 'warning';
    case 'disconnected':
    case 'unpaired':
      return 'error';
    default:
      return 'default';
  }
};

const getStatusColor = (status: string | undefined | null) => {
  if (!status || typeof status !== 'string') return '#9e9e9e';
  
  switch (status.toLowerCase()) {
    case 'running':
      return '#4caf50'; // green
    case 'completed':
      return '#2196f3'; // blue
    case 'containerstatusunknown':
      return '#9e9e9e'; // grey
    case 'error':
      return '#f44336'; // red
    case 'error↓':
      return '#d32f2f'; // darker red
    case 'oomkilled':
      return '#ff9800'; // orange
    case 'crashloopbackoff':
      return '#e91e63'; // pink
    case 'terminating':
      return '#9c27b0'; // purple
    default:
      return '#9e9e9e'; // grey
  }
};

const getStatusChip = (status: string | undefined | null) => {
  const displayStatus = status && typeof status === 'string' ? status : 'Unknown';
  return (
    <Box
      component="span"
      sx={{
        px: 2,
        py: 0.5,
        borderRadius: 1,
        bgcolor: getStatusColor(status),
        color: 'white',
        display: 'inline-block'
      }}
    >
      {displayStatus}
    </Box>
  );
};

export const PodDetailsDrawer: React.FC<PodDetailsDrawerProps> = ({ pod, open, onClose }) => {
  if (!pod) return null;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{ '& .MuiDrawer-paper': { width: { xs: '100%', sm: 400 } } }}
    >
      <Box sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h6">Pod Details</Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="h6" gutterBottom>
            {pod.name}
          </Typography>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle1" gutterBottom>
            Pod Information
          </Typography>
          <List dense>
            <ListItem>
              <ListItemText primary="Status" secondary={pod.statusString} />
            </ListItem>
            <ListItem>
              <ListItemText primary="Ready State" secondary={pod.readyState} />
            </ListItem>
            <ListItem>
              <ListItemText primary="Version" secondary={pod.version} />
            </ListItem>
            {pod.nodeName && (
              <ListItem>
                <ListItemText 
                  primary="Node" 
                  secondary={
                    <Box>
                      <Typography variant="body2">{pod.nodeName}</Typography>
                      {pod.nodeMachineType && (
                        <Typography variant="caption" color="text.secondary">
                          Machine Type: {pod.nodeMachineType}
                        </Typography>
                      )}
                    </Box>
                  } 
                />
              </ListItem>
            )}
          </List>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle1" gutterBottom>
            Container Status
          </Typography>
          <List dense>
            {(pod.containerStatuses || []).map((container, index) => (
              <ListItem key={container.containerID || index}>
                <ListItemText 
                  primary={`Container ${index + 1}`} 
                  secondary={
                    <React.Fragment>
                      <Typography component="span" variant="body2" color="text.primary">
                        Status: {getContainerStateString(container.state)}
                      </Typography>
                      <br />
                      <Typography component="span" variant="body2">
                        Ready: {container.ready ? 'Yes' : 'No'}
                      </Typography>
                      <br />
                      <Typography component="span" variant="body2">
                        Restart Count: {container.restartCount}
                      </Typography>
                    </React.Fragment>
                  }
                />
              </ListItem>
            ))}
            {(!pod.containerStatuses || pod.containerStatuses.length === 0) && (
              <ListItem>
                <ListItemText primary="No container information available" />
              </ListItem>
            )}
          </List>

          {pod.sessions && pod.sessions.length > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="subtitle1" gutterBottom>
                Sessions ({pod.sessions.length})
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {pod.sessions.map((session) => (
                  <SessionDetailsCube key={session._id} session={session} pods={[pod]} />
                ))}
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Drawer>
  );
};

export default PodDetailsDrawer; 